import * as Dialog from '@radix-ui/react-dialog'
import { BrowserOS } from 'hooks/use_detect_os'
import { useOnKeyPress } from 'hooks/use_onkeypress'
import { MenuDeleteIcon, MenuFileOpenIcon } from 'images'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { getLightboxFilesForSelectedTopic } from 'reduxes/slices/topics/selectors'

import { css, styled } from 'design'
import { Button, buttonStyles } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import {isLiveChatApp} from "../../../reduxes/slices/live_chat_app/selectors";
import {useSelectorDelegateStore} from "../../../reduxes/stores/root_store/hooks";
import { Timestamp } from '../../timekeeping/timestamp'
import { FileData } from './types'
import {SPEAKEASY_CONTAINER_ID} from "../../../pages/live_chat/constants";

export type LightboxProps = {
  data: FileData
  setData: Dispatch<SetStateAction<FileData | undefined>>
}

const preventDefault = (event: Event) => {
  event.preventDefault()
}

export const Lightbox = ({ data, setData }: LightboxProps) => {
  const [firstImage, setFirstImage] = useState<boolean>()
  const files: FileData[] = useSelector(getLightboxFilesForSelectedTopic)
  const closeTriggerRef = useRef<HTMLButtonElement>(null)
  const [container, setContainer] = React.useState<HTMLElement | null>(null)
  const nextImagePreload = useRef<HTMLImageElement>()
  const prevImagePreload = useRef<HTMLImageElement>()
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  useEffect(() => {
    if (liveChatApp) {
      setContainer(document.getElementById(SPEAKEASY_CONTAINER_ID))
    }
  }, [liveChatApp])

  const indexes = useMemo(() => {
    return files.reduce((acc: any, file, index) => {
      acc[file.url] = index
      return acc
    }, {})
  }, [files])

  const [previousFile, currentFile, nextFile] = useMemo(() => {
    if (!data) {
      return [undefined, undefined, undefined]
    }

    const currentIndex = indexes[data.url]
    const current = files[currentIndex]

    const next =
      currentIndex - 1 >= 0 ? files[currentIndex - 1] : files[files.length - 1]
    const previous =
      currentIndex + 1 < files.length ? files[currentIndex + 1] : files[0]
    if (next) {
      nextImagePreload.current = new Image()
      nextImagePreload.current.src = next.url
    }

    if (previous) {
      prevImagePreload.current = new Image()
      prevImagePreload.current.src = previous.url
    }

    return [previous, current, next]
  }, [data, indexes, files])

  const onChangeData = useCallback(
    (newData?: FileData) => {
      if (!newData) {
        return
      }

      setData && setData(newData)
      setFirstImage && setFirstImage((prevState) => !prevState)
    },
    [setData],
  )

  const onPreviousFile = useCallback(
    () => onChangeData(previousFile),
    [onChangeData, previousFile],
  )
  const onNextFile = useCallback(
    () => onChangeData(nextFile),
    [onChangeData, nextFile],
  )
  const onCloseDialog = useCallback(() => {
    closeTriggerRef.current?.click()
  }, [])

  // Keyboard accessibility and control
  // Left Arrow  - previous file
  // Right Arrow - next file
  // Escape      - close lightbox (NOTE: As there is no Dialog.Trigger, this is done via onKeyPress)
  useOnKeyPress([[BrowserOS.Any, 'ArrowLeft']], [onPreviousFile])
  useOnKeyPress([[BrowserOS.Any, 'ArrowRight']], [onNextFile])
  useOnKeyPress([[BrowserOS.Any, 'Escape']], [onCloseDialog])

  const { filename, size, url, content_type, created_at, thumbnail } =
    currentFile || data || {}
  const isVideo = content_type?.startsWith('video')

  const transparentImg =
    // eslint-disable-next-line max-len
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAAA8CAQAAACQ9RH5AAAAM0lEQVR42u3NAQkAAAwDoK9/6ccYAy1griRisVgsFovFYrFYLBaLxWKxWCwWi8VisXgpfn1/AD0BCuuxAAAAAElFTkSuQmCC'

  return (
    <>
      {
        !liveChatApp && (
          <div className={portalContainerStyles()} ref={setContainer} />
        )
      }
      <Dialog.Root
        defaultOpen
        onOpenChange={(open) => {
          if (open) {
            return
          }
          setData && setData(undefined)
        }}
        modal={false}
      >
        <Dialog.Portal container={container}>
          <Dialog.Content
            className={contentStyles({liveChatApp})}
            onOpenAutoFocus={(event: Event) => {
              event.preventDefault()
              closeTriggerRef.current?.focus()
            }}
            onPointerDownOutside={preventDefault}
            onInteractOutside={preventDefault}
          >
            <Stack gap="3" css={{ padding: '0 $4', height: '100%' }}>
              <Stack
                as="header"
                direction="row"
                css={{
                  width: '100%',
                  justifyContent: 'space-between',
                  padding: '$3 0 0',
                }}
              >
                <Stack css={{ flex: 1 }}>
                  <StyledTitle>{filename}</StyledTitle>
                  <StyledDescription>
                    <span>{size}</span>
                    <Timestamp date={created_at} />
                  </StyledDescription>
                </Stack>

                <Stack direction="row" gap="3">
                  <a
                    className={cn(
                      buttonStyles({
                        shape: 'square',
                        variant: 'secondary',
                      }),
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={url}
                  >
                    <MenuFileOpenIcon />
                  </a>
                  <Dialog.Close asChild ref={closeTriggerRef}>
                    <Button shape="square" aria-label="Close">
                      <MenuDeleteIcon />
                    </Button>
                  </Dialog.Close>
                </Stack>
              </Stack>

              <hr className={hrStyles()} />

              <main className={mainStyles()}>
                <Stack
                  direction="row"
                  gap="4"
                  css={{
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Button
                    onClick={onPreviousFile}
                    shape="square"
                    disabled={!previousFile}
                    aria-label="Show previous file"
                  >
                    <i className="fas fa-chevron-left" />
                  </Button>

                  <div className={imageContainerStyles()}>
                    {isVideo ? (
                      <>
                      <video
                        controls
                        className={responsiveImageStyles()}
                        autoPlay
                        playsInline
                        hidden={!firstImage}
                      >
                        <source src={firstImage ? url : transparentImg} type="video/mp4" ></source>
                      </video>
                      <video
                        controls
                        className={responsiveImageStyles()}
                        autoPlay
                        playsInline
                        hidden={firstImage}
                      >
                        <source src={!firstImage ? url : transparentImg} type="video/mp4" ></source>
                      </video>
                      </>
                    ) : (
                      <button
                        onClick={onCloseDialog}
                        type="button"
                        className={closeFromImageButtonStyles()}
                        aria-label="Zoom out"
                      >
                        <img
                          src={firstImage ? url : transparentImg}
                          alt={filename}
                          className={responsiveImageStyles()}
                          id={url}
                          hidden={!firstImage}
                          style={{
                            backgroundImage: `url('${
                              firstImage ? thumbnail : transparentImg
                            }')`,
                          }}
                        />
                        <img
                          src={!firstImage ? url : transparentImg}
                          alt={filename}
                          className={responsiveImageStyles()}
                          id={url}
                          hidden={firstImage}
                          style={{
                            backgroundImage: `url('${
                              !firstImage ? thumbnail : transparentImg
                            }')`,
                          }}
                        />
                      </button>
                    )}
                  </div>

                  <Button
                    onClick={onNextFile}
                    shape="square"
                    disabled={!nextFile}
                    aria-label="Show next file"
                  >
                    <i className="fas fa-chevron-right" />
                  </Button>
                </Stack>
              </main>
            </Stack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

const portalContainerStyles = css({
  position: 'fixed',
  pointerEvents: 'none',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2,

  '> *': {
    pointerEvents: 'auto',
  },
})

const contentStyles = css({
  position: 'absolute !important',
  top: 48,
  left: 0,
  // bottom: 75,
  bottom: 0,
  right: 0,
  backgroundColor: '$backgroundSecondary',
  zIndex: 10,

  '@md': {
    left: 66,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    willChange: 'transform',
  },
  '&:focus': { outline: 'none' },

  variants: {
    liveChatApp: {
      true: {
        inset: 0,
      },
    },
  },
})

const StyledTitle: any = styled(Dialog.Title, {
  fontSize: '$4',
  fontWeight: '400',
})

const StyledDescription: any = styled(Dialog.Description, {
  color: '$grey700',
  '> * + *': {
    marginLeft: '$3',
  },
})

const hrStyles = css({
  border: 'none',
  borderTop: '1px solid $grey100',
})

const mainStyles = css({
  display: 'flex',
  width: '100%',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
})

const imageContainerStyles = css({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const closeFromImageButtonStyles = css({
  border: 'none',
  background: 'none',
  cursor: 'zoom-out',
})

const responsiveImageStyles = css({
  maxWidth: '100%',
  maxHeight: '70vh',
  objectFit: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
})
